import './oligo-summary-bar.scss';

import ngRedux from 'ng-redux';

import * as Model from '../../models';
import * as I18n from '../../models/i18n';
import { AmountFormatter, formatAmount } from '../../models/util';

import { AddToCartResponse, CartService } from '../../service/cart-service';

type OligoSummaryData = {
  price?: Model.Pricing;
  totalOligos: number;
  maxNoOfOligos: number;
  valid: boolean;
  appState: Model.AppState;
};

interface OligoSummaryBarProperties {
  data: OligoSummaryData;
  projects: Model.ProjectItem[];
  priceProcessing?: boolean;
  cartProcessing?: boolean;
  cartRedirectDelaySecs?: number;
  productFamilySku?: string;
  onCartStateChange?(change: { state: Model.CartState }): void;
}

type I18nAware = {
  language: string;
  country: string;
  ADD_TO_CART_LABEL: string;
  OLIGO_COUNT_LABEL_TPL: string;
  OLIGO_COUNT_LABEL: string;
  TOTAL_AMOUNT_LABEL: string;
  MAX_OLIGO_EXCEEDED_HINT: string;
  INVALID_OLIGO_HINT: string;
};

export class OligoSummaryBarController implements ng.IComponentController, I18nAware, OligoSummaryBarProperties {

  public data: OligoSummaryData = {
    totalOligos: 0,
    valid: true,
    maxNoOfOligos: 200,
    appState: Model.AppStates.DRAFT,
  };
  public projects: Model.ProjectItem[] = [];
  public priceProcessing?: boolean = false;
  public cartProcessing?: boolean;
  public cartRedirectDelaySecs?: number;
  public productFamilySku?: string;
  public onCartStateChange?: (change: { state: Model.CartState }) => void;
  public formatAmount: AmountFormatter = formatAmount;

  public language: string = 'en';
  public country: string = 'us';
  public ADD_TO_CART_LABEL: string = 'Add to Cart';
  public OLIGO_COUNT_LABEL_TPL: string = '{{OLIGO_COUNT}} Oligo';
  public OLIGO_COUNT_LABEL: string = '';
  public TOTAL_AMOUNT_LABEL: string = 'Total';
  public MAX_OLIGO_EXCEEDED_HINT: string = `You have exceeded the maximum limit of {{${I18n.PlaceHolderKeys.MAX_OLIGO_COUNT}}} oligos. Please review your oligo entries.`;
  public INVALID_OLIGO_HINT: string = 'There are one or more invalid oligo configurations. Please review your oligo entries.';

  constructor(private $scope: ng.IScope, private $window: ng.IWindowService, $ngRedux: ngRedux.INgRedux, private cartService: CartService | null) {
    const unsubscribe = $ngRedux.connect(this.mapStateToThis.bind(this))(this);
    $scope.$on('$destroy', () => {
      unsubscribe();
      this.cartService = null;
    });
  }

  public addToCart(): void {
    if (!this.data || !this.projects || !this.cartService) {
      return;
    }
    if (this.cannotAddToCart()) {
      return;
    }
    const { projects } = this;
    const cartRedirectDelaySecs: number = this.cartRedirectDelaySecs || Model.WORLD_INITIAL.clientConfig.cartRedirectDelaySecs;
    const productFamilySku: string = this.productFamilySku || Model.WORLD_INITIAL.clientConfig.productFamilySku;
    this.cartProcessing = true;
    this.updateCartState(Model.CartStates.PROCESSING);
    this.cartService.addToCart({ projects }).then((response: AddToCartResponse) => {
      const { errors, cartURL } = response;
      const hasErrors: boolean = errors && errors.length > 0;
      if (hasErrors) {
        this.cartProcessing = false;
        this.updateCartState(Model.CartStates.FAILED);
        return;
      }
      this.$scope.$emit(Model.ScopeEvents.CART_ADD, projects, productFamilySku);
      setTimeout(() => {
        this.$window.location.href = cartURL;
      }, (cartRedirectDelaySecs || 0) * 1000);
    }, (error: object) => {
      this.cartProcessing = false;
      this.updateCartState(Model.CartStates.FAILED);
      this.$scope.$emit(Model.ScopeEvents.ERROR, error);
    });
  }

  public cannotAddToCart(): boolean {
    const { data } = this;
    if (!data) {
      return true;
    }
    return !data.valid || data.totalOligos <= 0 || (data.totalOligos > data.maxNoOfOligos);
  }

  private updateCartState(state: Model.CartState): void {
    if (!this.onCartStateChange) {
      return;
    }
    this.onCartStateChange({ state });
  }

  private mapStateToThis = (state: Model.RootState): OligoSummaryBarProperties & I18nAware => {
    const { projects, world } = state;
    const { language, maxNoOfOligos, appState, clientConfig, countryCode: country } = world;
    const { cartRedirectDelaySecs, productFamilySku } = clientConfig;
    const data: OligoSummaryData = state.projects.reduce((acc1: OligoSummaryData, project: Model.ProjectItem): OligoSummaryData => {
      return project.oligos.reduce((acc2: OligoSummaryData, oligo: Model.OligoItem): OligoSummaryData => {
        const { pricing, definition } = oligo;
        const { price: totalPrice } = acc2;
        if (pricing) {
          if (totalPrice) {
            totalPrice.available = totalPrice.available && pricing.available;
            totalPrice.amount = totalPrice.amount + pricing.amount;
            totalPrice.loading = totalPrice.loading || pricing.loading;
          } else {
            acc2.price = { ...pricing };
          }
        }
        acc2.valid = acc2.valid && definition.status.state === Model.OligoStates.VALID;
        acc2.totalOligos++;
        acc2.totalOligos = acc2.totalOligos + definition.advanced.alternate.selected.length;
        return acc2;
      }, acc1);
    }, { appState, maxNoOfOligos, totalOligos: 0, valid: appState === Model.AppStates.DRAFT });

    const { vocabulary } = world;
    const OLIGO_COUNT_LABEL_TPL: string = vocabulary[I18n.DictionaryKeys.APP_FOOTER_OLIGO_COUNT_TITLE] || this.OLIGO_COUNT_LABEL_TPL;
    let maxOligoExceededHint: string = vocabulary[I18n.DictionaryKeys.APP_FOOTER_ADD_TO_CART_EXCEED_MAX_OLIGO_HINT] || this.MAX_OLIGO_EXCEEDED_HINT;
    maxOligoExceededHint = I18n.replacePlaceHolders(maxOligoExceededHint, { [I18n.PlaceHolderKeys.MAX_OLIGO_COUNT] : `${maxNoOfOligos}` });
    return {
      data,
      language,
      country,
      projects,
      priceProcessing: data.price ? data.price.loading : false,
      cartRedirectDelaySecs,
      productFamilySku,
      OLIGO_COUNT_LABEL_TPL,
      ADD_TO_CART_LABEL: vocabulary[I18n.DictionaryKeys.APP_FOOTER_ADD_TO_CART_BUTTON] || this.ADD_TO_CART_LABEL,
      OLIGO_COUNT_LABEL: I18n.replacePlaceHolders(OLIGO_COUNT_LABEL_TPL, { [I18n.PlaceHolderKeys.OLIGO_COUNT]: `${data.totalOligos}` }),
      TOTAL_AMOUNT_LABEL: vocabulary[I18n.DictionaryKeys.APP_FOOTER_TOTAL_PRICE_TITLE] || this.TOTAL_AMOUNT_LABEL,
      INVALID_OLIGO_HINT: vocabulary[I18n.DictionaryKeys.APP_FOOTER_ADD_TO_CART_INVALID_OLIGO_HINT] || this.INVALID_OLIGO_HINT,
      MAX_OLIGO_EXCEEDED_HINT: maxOligoExceededHint,
    };
  }

}

export const OLIGO_SUMMARY_BAR_NAME: string = 'oligoSummaryBar';

export const OligoSummaryBar: ng.IDirectiveFactory = () => {
  return {
    template: require('./oligo-summary-bar.tpl.html'),
    controller: OligoSummaryBarController,
    controllerAs: 'oligoSummary',
    bindToController: true,
    restrict: 'E',
    transclude: true,
    scope: {
      onCartStateChange: '&',
      cartProcessing: '<',
    },
    link: ($scope: ng.IScope, $elem: JQLite, attrs: ng.IAttributes, ctrl?: ng.IController) => {
      const controller: OligoSummaryBarController = ctrl as OligoSummaryBarController;
      let addToCartBtnHintJQ: Model.BootstrapJQ | null;
      const unwatchOligoSummary = $scope.$watch(() => controller.data, (summaryData: OligoSummaryData) => {
        if (!addToCartBtnHintJQ) {
          const addOligoHintPopover: Model.BootstrapJQ = $elem.find('.btn.btn-outline-primary') as Model.BootstrapJQ;
          if (typeof addOligoHintPopover.popover === 'function') {
            addToCartBtnHintJQ = addOligoHintPopover;
          }
        }
        if (addToCartBtnHintJQ) {
          const exceededThreshold: boolean = summaryData.totalOligos > summaryData.maxNoOfOligos;
          const hasInvalidOligos: boolean = !summaryData.valid && (summaryData.appState === Model.AppStates.DRAFT);
          addToCartBtnHintJQ.popover('destroy');
          if (exceededThreshold || hasInvalidOligos) {
            const { MAX_OLIGO_EXCEEDED_HINT, INVALID_OLIGO_HINT } = controller;
            const content: string = exceededThreshold ? MAX_OLIGO_EXCEEDED_HINT : INVALID_OLIGO_HINT;
            const popover: Model.BootstrapPopoverOptions = {
              content,
              html: true,
              placement: 'top',
              trigger: 'hover',
            };
            addToCartBtnHintJQ.popover(popover);
          }
        }
      });
      $scope.$on('$destroy', () => {
        unwatchOligoSummary();
        if (addToCartBtnHintJQ) {
          addToCartBtnHintJQ.popover('destroy');
          addToCartBtnHintJQ = null;
        }
      });
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/components/oligo-summary-bar/oligo-summary-bar.ts